// AllMsbForTransactions.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const AllMsbForTransactions = () => {
  const [msbUsers, setMsbUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLevel, setUserLevel] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const token = sessionStorage.getItem('token');

  const fetchMsbUsers = useCallback(async (currentUserId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/users/all/${currentUserId}?level=4&status=active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setMsbUsers(response.data || []);
      } else {
        console.error('Failed to fetch MSB users');
        setError('Failed to fetch MSB users. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching MSB users:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view MSB users.');
      } else if (error.response?.status === 404) {
        setError('No MSB users found.');
      } else {
        setError('An error occurred while fetching MSB users. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('No authentication token found. Please login again.');
      setLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);

      if (decodedToken.level === 5) {
        setError('You are not allowed to view this page.');
        setLoading(false);
        return;
      } else if (decodedToken.level === 4) {
        navigate(`/msbReport/transactions/msb-transaction/${decodedToken.userId}`);
        return;
      } else if (decodedToken.level <= 3) {
        fetchMsbUsers(decodedToken.userId);
      } else {
        setError('Invalid user level.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      setError('Invalid authentication token. Please login again.');
      setLoading(false);
    }
  }, [token, navigate, fetchMsbUsers]);

  const handleViewTransactions = (msbUserId) => {
    navigate(`/msbReport/transactions/msb-transaction/${msbUserId}`);
  };

  const getUserLevelText = (level) => {
    switch(level) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return 'Unknown';
    }
  };

  const filteredUsers = msbUsers.filter(u => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase().trim();
    return (
      (u.firstName || '').toLowerCase().includes(q) ||
      (u.lastName || '').toLowerCase().includes(q) ||
      (u.phoneNumber || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.address || '').toLowerCase().includes(q)
    );
  });

  if (error) {
    return (
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />
        <Card sx={{ maxWidth: 600, margin: '0 auto', mt: 4, borderRadius: 3 }}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">Access Denied</Typography>
            </Alert>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
            {userLevel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your current level: {getUserLevelText(userLevel)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />
        
        <Paper elevation={3} sx={{ p: 3, pr: 4, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Q-PAY Centre (MSB) Transaction Reports
            </Typography>
            <Chip 
              label={`Logged in as: ${getUserLevelText(userLevel)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              Select a Q-PAY Centre (MSB) user to view detailed transaction & commission reports for all shops under their supervision.
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              placeholder="Search MSB by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ maxWidth: 400, width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress color="primary" size={50} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading MSB users...
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
              <Table aria-label="MSB Users Table">
                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Sl.No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>First Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone Number</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user, index) => (
                    <TableRow 
                      key={user._id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f1f5f9' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{user.firstName || 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{user.lastName || 'N/A'}</TableCell>
                      <TableCell>{user.phoneNumber || 'N/A'}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word' }}>{user.address || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleViewTransactions(user._id)}
                          sx={{
                            height: 32,
                            my: 'auto',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2
                          }}
                        >
                          View Transactions
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                          No MSB users found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </div>
  );
};

export default AllMsbForTransactions;